import { useState } from "react";
import { MessageCircle, X, ChevronRight } from "lucide-react";
import { trackShare } from "@/hooks/useAnalytics";

/**
 * WhatsAppShareBanner - Prominent floating banner to encourage WhatsApp sharing
 * Shows on homepage after scroll, dismissable
 */

// Generate UTM link
function generateUTMLink(source, campaign) {
  const baseUrl = typeof window !== 'undefined' 
    ? `${window.location.protocol}//${window.location.host}`
    : "https://www.upnextcalendars.com";
  
  const params = new URLSearchParams({
    utm_source: source,
    utm_medium: "share",
    utm_campaign: campaign,
    utm_content: new Date().toISOString().split('T')[0]
  });
  return `${baseUrl}?${params.toString()}`;
}

// Pre-formatted WhatsApp message
function getShareMessage() {
  const link = generateUTMLink("whatsapp", "group_share");
  return `🗓️ *UPNEXT - Event Malaysia 2026*

Korang, aku jumpa website best untuk cari event kat Malaysia!

✅ Semua event 2026 dalam satu tempat
✅ Konsert, festival, sukan, free events
✅ Boleh bookmark & set reminder

Jom check sama-sama 👇
${link}`;
}

export function WhatsAppShareBanner() {
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('wa_banner_dismissed') === 'true';
    }
    return false;
  });

  const handleShare = () => {
    const message = getShareMessage();
    trackShare("whatsapp", "homepage", "banner_cta", "Homepage Share Banner");
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  const handleDismiss = () => {
    setDismissed(true);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('wa_banner_dismissed', 'true');
    }
  };

  if (dismissed) return null;

  return (
    <div className="fixed bottom-4 right-4 w-56 sm:w-64 z-50 animate-in slide-in-from-bottom-5 duration-500">
      <div className="bg-gradient-to-r from-[#25D366] to-[#128C7E] rounded-xl shadow-2xl shadow-[#25D366]/30 overflow-hidden">
        {/* Close button */}
        <button 
          onClick={handleDismiss}
          className="absolute top-1.5 right-1.5 p-1 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>

        <div className="p-3">
          {/* Header */}
          <div className="flex items-center gap-2.5 mb-2.5 pr-5">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <MessageCircle className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className="text-white font-bold text-xs leading-tight">Share ke Group WhatsApp!</p>
              <p className="text-white/80 text-[11px]">Ajak kawan plan event</p>
            </div>
          </div>

          {/* CTA Button */}
          <button
            onClick={handleShare}
            className="w-full flex items-center justify-center gap-1.5 bg-white text-[#25D366] font-bold text-sm py-2 px-3 rounded-lg hover:bg-white/90 transition-all active:scale-[0.98] shadow"
            data-testid="wa-share-banner-btn"
          >
            <MessageCircle className="h-4 w-4" />
            <span>Hantar ke Group</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * WhatsAppFloatingButton - Small floating button always visible
 * Positioned below AI Chat button to avoid overlap
 */
export function WhatsAppFloatingButton() {
  const handleShare = () => {
    const message = getShareMessage();
    trackShare("whatsapp", "homepage", "floating_btn", "Floating WhatsApp Button");
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  return (
    <button
      onClick={handleShare}
      className="fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-[#25D366] hover:bg-[#128C7E] shadow-xl shadow-[#25D366]/40 flex items-center justify-center transition-all hover:scale-110 active:scale-95"
      aria-label="Share to WhatsApp"
      data-testid="wa-floating-btn"
    >
      <MessageCircle className="h-5 w-5 text-white" />
    </button>
  );
}

/**
 * WhatsAppShareCard - Inline card for sharing in content sections
 */
export function WhatsAppShareCard({ title = "Ajak kawan plan event!", subtitle = "Share website ni ke group WhatsApp korang" }) {
  const handleShare = () => {
    const message = getShareMessage();
    trackShare("whatsapp", "homepage", "inline_card", "Inline Share Card");
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  return (
    <div className="glass-card rounded-2xl p-6 border border-[#25D366]/30 bg-gradient-to-br from-[#25D366]/10 to-transparent">
      <div className="flex flex-col md:flex-row items-center gap-4">
        <div className="w-16 h-16 rounded-2xl bg-[#25D366] flex items-center justify-center shadow-lg shadow-[#25D366]/30">
          <MessageCircle className="h-8 w-8 text-white" />
        </div>
        <div className="flex-1 text-center md:text-left">
          <h3 className="text-lg font-bold text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <button
          onClick={handleShare}
          className="flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-bold py-3 px-6 rounded-xl transition-all hover:scale-105 active:scale-95 shadow-lg shadow-[#25D366]/30"
          data-testid="wa-share-card-btn"
        >
          <MessageCircle className="h-5 w-5" />
          <span>Share Sekarang</span>
        </button>
      </div>
    </div>
  );
}

export default WhatsAppShareBanner;
